"use client";
import { ModalModifierSelection } from "@/app/(shop)/services/[slug]/components/modalModifierSelection";
import { useCurrency } from "@/app/providers/currencyContext";
import { useMerchant } from "@/app/providers/merchantProvider";
import { AgeRestricted } from "@/components/ageRestricted/ageRestricted";
import { DeliveryModalError } from "@/components/deliveryModalError/deliveryModalError";
import DisableOverlay from "@/components/disableOverlay/disableOverlay";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import { ModalSpecialInstructions } from "@/components/modalSpecialInstructions/modalSpecialInstructions";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { getOrderEstimate } from "@/dataLayer";
import { Address } from "@/dto/address";
import { DayPart, DayPartMenu } from "@/dto/dayPartMenu";
import { ImageType } from "@/dto/image";
import { Item } from "@/dto/item";
import { Menu } from "@/dto/menu";
import { Modifier } from "@/dto/modifier";
import { SelectedModifiers } from "@/dto/selectedModifiers";
import { TableData } from "@/dto/tableData";
import { RestaurantCartItem } from "@/models/cart";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { findNextAvailableDay } from "@/utils/dayPartMenuUtils";
import { getPrice } from "@/utils/getPrice";
import { lightenColor } from "@/utils/lightenColor";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import {
  calculateModifierQuantityChange,
  calculateModifierSelection,
  checkIsMinusDisabled,
  checkIsPlusDisabled,
  getAvailabilityMessage,
  getDeliveryServiceStatus,
  getSelectableModifierGroups,
  isAddButtonDisabled,
  isModifierSelectionValid,
  mapCartModifiersToState,
  prepareCartItem,
} from "@/utils/restaurantModalModifierSelectionUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { isEmpty, isNil } from "lodash";
import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  Dispatch,
  Ref,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Hours } from "@/dto/hours";
import {
  createSchedulingContext,
  getSchedulableDateOptions,
} from "@/utils/scheduleOrderUtils";
import { OrderType } from "@/constants/enum";
import { ScheduledTime } from "@/dto/scheduledTime";
import { OrderEstimates } from "@/dto/orderEstimates";
import { ItemMarkersChipContainer } from "@/components/itemMarkersChip/itemMarkersChip";

const MAX_QUANTITY = 100;
const MIN_QUANTITY = 1;

interface RestaurantItemModalModifierSelectionProps {
  isCheckoutEnabled: boolean;
  onRequestClose: () => void;
  productImage: ImageType;
  item: Item;
  modifiers: Modifier[];
  selectedModifiers: SelectedModifiers;
  setSelectedModifiers: Dispatch<SetStateAction<SelectedModifiers>>;
  menu: Menu;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  isAcceptingOrders: boolean;
  merchantColor: string;
  isRunningTabsEnabled: boolean;
  isMobileScreen: boolean;
  isEssentials: boolean;
  isMerchantOpen: boolean;
  selectedDayPart: DayPart | null;
  timezone: string;
  editingCartId: string | null;
  businessHours: Hours | undefined;
  imageRef?: Ref<HTMLImageElement>;
  scrollContainerRef?: Ref<HTMLDivElement>;
  titleId?: string;
  subtitleId?: string;
}

export const RestaurantItemModalModifierSelection = ({
  isCheckoutEnabled,
  onRequestClose,
  productImage,
  item,
  modifiers,
  selectedModifiers,
  setSelectedModifiers,
  menu,
  quantity,
  setQuantity,
  isAcceptingOrders,
  merchantColor,
  isRunningTabsEnabled,
  isMobileScreen,
  isEssentials,
  isMerchantOpen,
  selectedDayPart,
  timezone,
  editingCartId,
  businessHours,
  imageRef,
  scrollContainerRef,
  titleId,
  subtitleId,
}: RestaurantItemModalModifierSelectionProps) => {
  const { merchant } = useMerchant();
  const t = useTranslations("restaurantMenu");
  const tPickup = useTranslations("CalculatePickTime");
  const [showSpecialInstructions, setShowSpecialInstructions] =
    useState<boolean>(false);
  const [showDeliveryConfirmationModal, setShowDeliveryConfirmationModal] =
    useState(false);
  const [tempAddress, setTempAddress] = useState<Address | null>(null);
  const [showDeliveryErrorModal, setShowDeliveryErrorModal] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [isAddtocartClicked, setIsAddtocartClicked] = useState<boolean>(false);
  const tableData = getFromLocalStorage<TableData | null>(
    LocalStorageKey.TABLE_DATA,
    null
  );
  const isEditMode = !!editingCartId;

  useEffect(() => {
    const storedCart = getFromLocalStorage<RestaurantCartItem[] | undefined>(
      LocalStorageKey.RESTAURANT_CART,
      undefined
    );
    if (storedCart) {
      const itemToEdit = storedCart.find(
        (item) => item?.key === editingCartId
      )?.value;
      if (itemToEdit) {
        setQuantity(itemToEdit.quantity);
        setSelectedModifiers(
          mapCartModifiersToState(itemToEdit.modifiers, modifiers)
        );
        if (itemToEdit.specialInstructions) {
          setShowSpecialInstructions(!!itemToEdit.specialInstructions);
          setSpecialInstructions(itemToEdit.specialInstructions);
        }
      }
    }
  }, [editingCartId]);

  const { filteredModifierGroups, selectableModifierGroups } = useMemo(
    () => getSelectableModifierGroups(menu, item, modifiers),
    [menu, item, modifiers]
  );

  const allModifiers: Modifier[] = menu.modifiers;

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    setIsAddtocartClicked(false);

    setSelectedModifiers((prev: any) =>
      calculateModifierSelection(
        groupId,
        modifierId,
        isSingleSelection,
        modifiers,
        selectableModifierGroups,
        prev
      )
    );
  };

  const handleModifierQuantityChange = (
    groupId: string,
    modifierId: string,
    newQuantity: number
  ) => {
    setIsAddtocartClicked(false);

    setSelectedModifiers((prev: any) =>
      calculateModifierQuantityChange(
        groupId,
        modifierId,
        newQuantity,
        selectableModifierGroups,
        prev
      )
    );
  };

  const validateModifierSelection = () => {
    return isModifierSelectionValid(
      selectableModifierGroups,
      selectedModifiers
    );
  };

  let addButtonDisabled: boolean =
    isAddButtonDisabled(item, isAcceptingOrders) ||
    tableData?.isValid === false;

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= MIN_QUANTITY && newQuantity <= MAX_QUANTITY) {
      setQuantity(newQuantity);
    }
  };

  const [isMinusDisabled, setIsMinusDisabled] = useState(
    !isAcceptingOrders || quantity ? quantity <= MIN_QUANTITY : true
  );
  const [isPlusDisabled, setIsPlusDisabled] = useState(
    !isAcceptingOrders || quantity ? quantity >= MAX_QUANTITY : false
  );

  useEffect(() => {
    setIsMinusDisabled(
      checkIsMinusDisabled(isAcceptingOrders, quantity, MIN_QUANTITY)
    );
    setIsPlusDisabled(
      checkIsPlusDisabled(isAcceptingOrders, quantity, MAX_QUANTITY)
    );
  }, [quantity, isAcceptingOrders]);

  const handleAddToCart = () => {
    if (!validateModifierSelection()) {
      setIsAddtocartClicked(true);
      return;
    }

    const hasDeliveryAddressSet = getFromLocalStorage(
      LocalStorageKey.DELIVERY_ADDRESS,
      null
    );

    const { isOnlyDeliveryEnabled } = getDeliveryServiceStatus(merchant);

    if (!hasDeliveryAddressSet && isOnlyDeliveryEnabled) {
      setShowDeliveryConfirmationModal(true);
      return;
    }

    const sessionDayPartMenu = getFromSessionStorage<Partial<DayPartMenu>>(
      SessionStorageKey.DAYPART_MENU,
      {}
    );

    const newItem = prepareCartItem({
      item,
      quantity,
      selectedModifiers,
      selectableModifierGroups,
      filteredModifierGroups,
      allModifiers,
      specialInstructions,
      editingCartId,
      sessionDayPartMenu,
      getPrice, // Passing the dependency
    });

    RestaurantCart.addItem(newItem);

    RestaurantCart.triggerCartSync(isRunningTabsEnabled, tableData);
    onRequestClose();
    setQuantity(1);
    setSelectedModifiers({});
  };

  const onAddressRangeValidation = async (address: Address) => {
    // A check before adding a cart with an out-of-bounds address
    try {
      setShowDeliveryConfirmationModal(false);
      setTempAddress(address);
      await getOrderEstimate(merchant.id, address);

      saveToLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, address);
      handleAddToCart();
    } catch (error) {
      console.error("Error on address validation:: ", error);
      setShowDeliveryErrorModal(true);
    }
  };

  const baseContext = createSchedulingContext({
    merchant,
    orderType:
      getFromSessionStorage<Partial<ScheduledTime>>(
        SessionStorageKey.SCHEDULED_TIME,
        {}
      )?.orderType || OrderType.PICKUP,
    orderEstimates: getFromSessionStorage<Partial<OrderEstimates>>(
      SessionStorageKey.ORDER_ESTIMATES,
      {}
    ),
    daypartMenu: selectedDayPart,
    hoursOverride: businessHours ?? null,
  });

  const schedulableDateOptions = getSchedulableDateOptions({
    context: baseContext,
    scheduleDateRange: merchant?.scheduledOrdering.maxDays - 1,
    t: tPickup,
  });

  return (
    <div
      data-testid={applyDataTestId(pom.restaurantMenu.menuPageItem)}
      ref={scrollContainerRef}
      className="w-full h-full overflow-y-auto overflow-x-hidden flex flex-col sm:flex-row-reverse sm:overflow-y-hidden"
    >
      {productImage && (
        <div className="flex-shrink-0 w-full h-[282px] sm:w-[450px] sm:h-[576px]">
          <Image
            ref={imageRef}
            width={isMobileScreen ? 375 : 450}
            height={isMobileScreen ? 282 : 576}
            alt={item.name || t("itemName")}
            src={productImage.source}
            className="object-cover object-center w-full h-full"
            priority={true}
          />
        </div>
      )}

      <div
        className={`flex flex-col sm:min-h-[260px] w-full ${
          productImage ? "sm:w-[450px]" : "sm:w-[530px] pb-[64px]"
        } relative h-full sm:h-auto`}
      >
        <DisableOverlay
          merchantName={merchant.name}
          isCheckoutEnabled={
            isCheckoutEnabled && merchant.isBusinessHourAvailable
          }
        >
          <div className="px-6 pt-[14px] sm:pt-6 sticky top-0 bg-white shadow-modalStickyHeader"> 
            <ItemMarkersChipContainer
              markerIds={item?.markerIds ?? []}
              markers={menu?.markers}
              className="mt-12 sm:mt-10 pr-6 sm:pr-0"
            />
            <h2
              id={titleId}
              tabIndex={-1}
              aria-describedby={subtitleId}
              data-testid={applyDataTestId(pom.itemModal.name)}
              className="sm:pt-4 pr-6 sm:pr-0 text-[24px] leading-9 sm:text-[32px] sm:leading-10 font-medium text-[#373737] whitespace-normal overflow-hidden text-ellipsis focus:outline-none"
            >
              {item.name}
            </h2>
            {item.isAgeRestricted && (
              <AgeRestricted
                text={t("ageRestriction")}
                textClassName="text-[16px]"
              />
            )}
          </div>
          <div className="flex flex-1 flex-col px-6 sm:max-h-[430px] sm:overflow-y-auto pb-[128px] sm:pb-[0px]">
            <div className="py-4">
              <div
                id={subtitleId}
                className="text-gray-500 break-words whitespace-pre-wrap"
              >
                {item.description && <p>{item.description}</p>}
              </div>
            </div>
            {selectableModifierGroups.length > 0 && (
              <ModalModifierSelection
                {...{
                  selectableModifierGroups,
                  selectedModifiers,
                  setSelectedModifiers,
                  handleModifierChange,
                  handleModifierQuantityChange,
                  merchantColor,
                  isAddtocartClicked,
                  isEssentials,
                  isDisabled: addButtonDisabled,
                }}
              />
            )}
            {!isEssentials && (
              <ModalSpecialInstructions
                className="mb-3 sm:mb-12"
                {...{
                  showSpecialInstructions,
                  setShowSpecialInstructions,
                  specialInstructions,
                  merchantColor,
                  setSpecialInstructions,
                }}
              />
            )}
          </div>
          <RestaurantModalBottomButtons
            {...{
              merchantColor,
              quantity,
              handleQuantityChange,
              addButtonDisabled,
              item,
              selectedModifiers,
              handleAddToCart,
              allModifiers,
              filteredModifierGroups,
              isMinusDisabled,
              isPlusDisabled,
              isMerchantOpen,
              selectedDayPart,
              timezone,
              isEditMode,
              businessHours,
              canScheduleOrder: !isEmpty(schedulableDateOptions),
            }}
          />
        </DisableOverlay>
      </div>

      <EditAddressesModal
        isOpen={showDeliveryConfirmationModal}
        onRequestClose={() => setShowDeliveryConfirmationModal(false)}
        onUpdated={onAddressRangeValidation}
        titleKey="confirmationTitle"
        subtitleKey="confirmationSubTitle"
      />

      {/* For use when out of delivery range */}
      <DeliveryModalError
        onRequestClose={() => {
          setShowDeliveryErrorModal(false);
        }}
        isOpen={showDeliveryErrorModal}
        address={tempAddress || {}}
        onEnterNewAddress={() => {
          setShowDeliveryErrorModal(false);
          setShowDeliveryConfirmationModal(true);
        }}
        checkoutCloseHandler={() => {}}
      />
    </div>
  );
};

const RestaurantModalBottomButtons = ({
  merchantColor,
  quantity,
  handleQuantityChange,
  addButtonDisabled,
  item,
  selectedModifiers,
  handleAddToCart,
  isMinusDisabled,
  isPlusDisabled,
  filteredModifierGroups,
  allModifiers,
  isMerchantOpen,
  selectedDayPart,
  timezone,
  isEditMode,
  businessHours,
  canScheduleOrder,
}: any) => {
  const { format } = useCurrency();
  const t = useTranslations("RestaurantItemModalModifierSelection");

  const itemNotAvailableMessage = getAvailabilityMessage({
    addButtonDisabled,
    selectedDayPart,
    isMerchantOpen,
    timezone,
    t,
    HHMMtoDate,
    findNextAvailableDay,
    isEmpty,
    isNil,
    businessHours,
    canScheduleOrder,
  });

  return (
    <div className="fixed flex-col sm:flex-row sm:absolute bottom-0 left-0 w-full sm:h-[64px] flex z-[12]">
      {itemNotAvailableMessage ? (
        <div className="w-full h-[64px] text-[#373737] font-medium flex flex-row justify-center items-center bg-[#E6F3FF] px-6">
          {itemNotAvailableMessage}
        </div>
      ) : (
        <>
          <div
            style={{
              backgroundColor: lightenColor(merchantColor, 90),
            }}
            className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium h-[64px] w-full sm:w-2/5 flex flex-row"
          >
            <QuantityStepper
              handleQuantityChange={handleQuantityChange}
              isModalQuantity={true}
              max={MAX_QUANTITY}
              min={MIN_QUANTITY}
              quantity={quantity}
              disableMinus={isMinusDisabled}
              disablePlus={isPlusDisabled}
              merchantColor={merchantColor}
              adaName={item?.name}
            />
          </div>
          <button
            style={{
              backgroundColor: addButtonDisabled ? "#C7C7C7" : merchantColor,
            }}
            className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl h-[64px] font-medium w-full sm:w-3/5"
            onClick={handleAddToCart}
            disabled={addButtonDisabled}
            data-testid={applyDataTestId(pom.restaurantModal.addToCartButton)}
          >
            {item?.available === false ? (
              <div className="text-[#373737] text-[15px]">
                <p>{t("itemNotAvailable")}</p>
              </div>
            ) : (
              <div className="flex flex-row px-6 items-center justify-between">
                <p>{!isEditMode ? t("addToOrder") : t("update")}</p>
                <p>
                  {format(
                    getPrice({
                      item,
                      filteredModifierGroups,
                      allModifiers,
                      selectedModifiers,
                      quantity,
                    })
                  )}
                </p>
              </div>
            )}
          </button>
        </>
      )}
    </div>
  );
};
